import type { Bracket, Id, Match, Slot } from '../types.js';
import type { Rng } from '../rng.js';

/**
 * Single Elimination (docs/04 §6).
 * - Bracket-Größe = nächste Zweierpotenz, Freilose an die bestgesetzten Teams
 * - Standard-Setzung 1–16, 8–9, … über `seedPositions`
 * - Gruppentrennung: Teams derselben Gruppe treffen frühestens im Halbfinale
 *   aufeinander (best effort, Tausch nur innerhalb gleicher Qualifikationsstufe)
 */

export function bracketSize(n: number): number {
  let s = 1;
  while (s < n) s *= 2;
  return Math.max(2, s);
}

/** Bracket-Position → Seed. size 8 → [1,8,4,5,2,7,3,6] */
export function seedPositions(size: number): number[] {
  let order = [1];
  while (order.length < size) {
    const next: number[] = [];
    const m = order.length * 2 + 1;
    for (const s of order) next.push(s, m - s);
    order = next;
  }
  return order;
}

/** Runde (1-basiert), in der sich zwei Bracket-Positionen frühestens treffen */
export function meetingRound(posA: number, posB: number): number {
  let r = 1;
  let a = posA;
  let b = posB;
  while (a >> 1 !== b >> 1) {
    a >>= 1;
    b >>= 1;
    r++;
  }
  return r;
}

export interface Qualifier {
  teamId: Id;
  /** Qualifikationsstufe: 1 = Gruppensieger, 2 = Zweiter … (Tausch nur innerhalb Stufe) */
  tier: number;
  groupId?: Id;
}

/**
 * Ordnet Qualifikanten (bereits nach Stärke sortiert) den Seeds zu und
 * minimiert frühe Gruppen-Duelle durch Tausch innerhalb derselben Stufe.
 */
export function placeQualifiers(qualifiers: readonly Qualifier[], size: number, separateGroups: boolean, rng: Rng): (Qualifier | null)[] {
  const positions = seedPositions(size);
  const bySeed: (Qualifier | null)[] = Array.from({ length: size }, () => null);
  qualifiers.forEach((q, i) => {
    bySeed[i] = q;
  });
  // Position → Seed-Index
  const slotOf = (seedIdx: number): number => positions.indexOf(seedIdx + 1);
  const rounds = Math.log2(size);
  const semi = Math.max(1, rounds - 1);

  const conflicts = (arr: (Qualifier | null)[]): number => {
    let c = 0;
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        const a = arr[i];
        const b = arr[j];
        if (!a || !b || !a.groupId || a.groupId !== b.groupId) continue;
        const r = meetingRound(slotOf(i), slotOf(j));
        if (r < semi) c += semi - r;
      }
    }
    return c;
  };

  if (!separateGroups) return bySeed;
  let cur = conflicts(bySeed);
  for (let iter = 0; iter < 200 && cur > 0; iter++) {
    const i = rng.int(qualifiers.length);
    const q = bySeed[i];
    if (!q) continue;
    const sameTier = qualifiers.map((_, k) => k).filter((k) => k !== i && bySeed[k]?.tier === q.tier);
    if (sameTier.length === 0) continue;
    const j = sameTier[rng.int(sameTier.length)] as number;
    const tmp = bySeed[i] ?? null;
    bySeed[i] = bySeed[j] ?? null;
    bySeed[j] = tmp;
    const next = conflicts(bySeed);
    if (next <= cur) cur = next;
    else {
      bySeed[j] = bySeed[i] ?? null;
      bySeed[i] = tmp;
    }
  }
  return bySeed;
}

export interface BuiltBracket {
  bracket: Bracket;
  matches: Match[];
}

/**
 * Erzeugt Bracket + Matches. `seeded[i]` = Team mit Seed i+1 (null = Freilos).
 * Freilos-Spiele in Runde 1 werden nicht angelegt; das Team steht direkt in Runde 2.
 */
export function buildSingleElim(phaseId: Id, seeded: readonly (Id | null)[], opts: { thirdPlaceMatch: boolean; nextId: () => Id; firstNumber: number }): BuiltBracket {
  const size = bracketSize(seeded.length);
  const positions = seedPositions(size);
  const rounds = Math.log2(size);
  const slotAt = (pos: number): Slot => {
    const seedNo = positions[pos] as number;
    const team = seeded[seedNo - 1] ?? null;
    return team ? { kind: 'team', teamId: team } : { kind: 'bye' };
  };

  const matches: Match[] = [];
  const roundMatchIds: Id[][] = [];
  let number = opts.firstNumber;
  // Runde 1: Slots aus Positionen; „prev“ = Slot je Bracket-Knoten der Vorrunde
  let prev: Slot[] = Array.from({ length: size }, (_, p) => slotAt(p));
  for (let r = 1; r <= rounds; r++) {
    const ids: Id[] = [];
    const next: Slot[] = [];
    for (let k = 0; k < prev.length; k += 2) {
      const a = prev[k] as Slot;
      const b = prev[k + 1] as Slot;
      if (a.kind === 'bye' && b.kind !== 'bye') {
        next.push(b);
        continue;
      }
      if (b.kind === 'bye' && a.kind !== 'bye') {
        next.push(a);
        continue;
      }
      const id = opts.nextId();
      const both = a.kind === 'team' && b.kind === 'team';
      matches.push({ id, phaseId, round: r, number: number++, a, b, status: both ? 'ready' : 'open', priority: 0, bracketRole: 'main', source: { a, b } });
      ids.push(id);
      next.push({ kind: 'winnerOf', matchId: id });
    }
    roundMatchIds.push(ids);
    prev = next;
  }

  let thirdPlaceMatchId: Id | undefined;
  const semis = roundMatchIds[rounds - 2];
  if (opts.thirdPlaceMatch && semis && semis.length === 2) {
    const id = opts.nextId();
    thirdPlaceMatchId = id;
    const a: Slot = { kind: 'loserOf', matchId: semis[0] as Id };
    const b: Slot = { kind: 'loserOf', matchId: semis[1] as Id };
    matches.push({ id, phaseId, round: rounds, number: number++, a, b, status: 'open', priority: -1, bracketRole: 'third', source: { a, b } });
  }

  const bracket: Bracket = { size, rounds, seedOrder: positions, roundMatchIds };
  if (thirdPlaceMatchId) bracket.thirdPlaceMatchId = thirdPlaceMatchId;
  return { bracket, matches };
}

/**
 * Trägt Sieger/Verlierer eines beendeten Spiels in abhängige Slots ein.
 * Gibt die IDs der Matches zurück, die dadurch `ready` wurden.
 */
export function propagate(matches: Match[], finished: Match): Id[] {
  if (!finished.result || !finished.result.winner) return [];
  const winner = finished.result.winner === 'A' ? finished.a : finished.b;
  const loser = finished.result.winner === 'A' ? finished.b : finished.a;
  if (winner.kind !== 'team' || loser.kind !== 'team') return [];
  const ready: Id[] = [];
  for (const m of matches) {
    let changed = false;
    for (const side of ['a', 'b'] as const) {
      const s = m[side];
      if (s.kind === 'winnerOf' && s.matchId === finished.id) {
        m[side] = { kind: 'team', teamId: winner.teamId };
        changed = true;
      } else if (s.kind === 'loserOf' && s.matchId === finished.id) {
        m[side] = { kind: 'team', teamId: loser.teamId };
        changed = true;
      }
    }
    if (changed && m.status === 'open' && m.a.kind === 'team' && m.b.kind === 'team') {
      m.status = 'ready';
      ready.push(m.id);
    }
  }
  return ready;
}

/**
 * Macht `propagate` rückgängig (Ergebniskorrektur): Slots, die von diesem Spiel
 * abhängen, werden wieder zu Platzhaltern. Fehler, wenn ein abhängiges Spiel
 * schon läuft oder beendet ist.
 */
export function unpropagate(matches: Match[], reopened: Match): void {
  for (const m of matches) {
    if (!m.source) continue;
    for (const side of ['a', 'b'] as const) {
      const src = m.source[side];
      if ((src.kind !== 'winnerOf' && src.kind !== 'loserOf') || src.matchId !== reopened.id) continue;
      if (m[side].kind !== 'team') continue;
      if (m.status === 'running' || m.status === 'finished' || m.status === 'walkover') {
        throw new Error(`Spiel ${m.number} hängt davon ab und ist schon gestartet`);
      }
      m[side] = src;
      // Ein abgesagtes Spiel (z. B. Spiel um Platz 3 abgewählt) bleibt abgesagt
      if (m.status !== 'cancelled') m.status = 'open';
    }
  }
}
